import React from 'react';
import { aboutSection, images } from '../mock';
import { Target, Users, Award } from 'lucide-react';

export const About = () => {
  return (
    <section id="about" style={{
      background: '#3A4149',
      padding: '6rem 1.5rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Subtle Mountain Background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url(https://images.unsplash.com/photo-1486870591958-9b9d0d1dda99?w=1920&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.08
      }} />
      
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: '4rem', maxWidth: '800px', margin: '0 auto 4rem' }}>
          <h2 style={{ 
            fontFamily: "'Playfair Display', Georgia, serif",
            marginBottom: '1rem', 
            color: '#FFFFFF',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 700,
            fontStyle: 'italic'
          }}>
            {aboutSection.heading}
          </h2>
          <p style={{ 
            fontFamily: "'Playfair Display', Georgia, serif",
            color: '#7EC0D6', 
            fontWeight: 600,
            fontSize: 'clamp(1.25rem, 2vw, 1.5rem)',
            fontStyle: 'italic'
          }}>
            {aboutSection.tagline}
          </p>
        </div>

        {/* Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '3rem',
          alignItems: 'center',
          marginBottom: '4rem'
        }} className="about-grid">
          {/* Image */}
          <div style={{
            borderRadius: '1rem',
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)'
          }}>
            <img 
              src={images.team} 
              alt="Healthcare team collaboration"
              style={{
                width: '100%',
                height: '400px',
                objectFit: 'cover'
              }}
            />
          </div>

          {/* Text Content */}
          <div>
            <p style={{ fontFamily: "'Open Sans', sans-serif", marginBottom: '1.5rem', lineHeight: '1.8', color: '#E0E0E0' }}>
              {aboutSection.mission}
            </p>
            <p style={{ fontFamily: "'Open Sans', sans-serif", marginBottom: '1.5rem', lineHeight: '1.8', color: '#E0E0E0' }}>
              {aboutSection.experience}
            </p>
            <p style={{ fontFamily: "'Open Sans', sans-serif", marginBottom: '1.5rem', lineHeight: '1.8', color: '#E0E0E0' }}>
              {aboutSection.philosophy}
            </p>
            <p style={{ fontFamily: "'Playfair Display', Georgia, serif", marginBottom: '1.5rem', lineHeight: '1.8', fontWeight: 600, color: '#7EC0D6', fontStyle: 'italic', fontSize: '1.125rem' }}>
              {aboutSection.belief}
            </p>
            <p style={{ fontFamily: "'Open Sans', sans-serif", lineHeight: '1.8', color: '#E0E0E0' }}>
              {aboutSection.commitment}
            </p>
          </div>
        </div>

        {/* Value Props */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '2rem',
          marginTop: '4rem'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            background: 'rgba(92, 112, 128, 0.4)',
            borderRadius: '1rem',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: 'rgba(0, 191, 255, 0.2)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <Target size={32} color="#00BFFF" />
            </div>
            <h3 style={{ fontFamily: "'Open Sans', sans-serif", marginBottom: '0.75rem', color: '#FFFFFF', fontWeight: 600, fontSize: '1.25rem' }}>Our Mission</h3>
            <p style={{ fontFamily: "'Open Sans', sans-serif", lineHeight: '1.7', color: '#E0E0E0', fontSize: '0.95rem' }}>Our mission is rooted in compassion and scale. We believe that by partnering with recovery programs, we can reach those who need it most—supporting not only the dedicated professionals who serve as daily heroes in this field, but also the courageous individuals walking the challenging path of addiction recovery. Every person we touch through these partnerships represents a life transformed and a future reclaimed.</p>
          </div>

          <div style={{
            textAlign: 'center',
            padding: '2rem',
            background: 'rgba(92, 112, 128, 0.4)',
            borderRadius: '1rem',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: 'rgba(126, 192, 214, 0.2)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <Users size={32} color="#7EC0D6" />
            </div>
            <h3 style={{ fontFamily: "'Open Sans', sans-serif", marginBottom: '0.75rem', color: '#FFFFFF', fontWeight: 600, fontSize: '1.25rem' }}>Our Network</h3>
            <p style={{ fontFamily: "'Open Sans', sans-serif", lineHeight: '1.7', color: '#E0E0E0', fontSize: '0.95rem' }}>Over seven years, we have built meaningful relationships with health-conscious businesses and individuals across North America and Latin America. These connections form a community united by a common belief: that cellular health is the foundation for lasting wellness. Now, we are extending this network to include recovery programs, bringing proven solutions to those working on the frontlines of healing.</p>
          </div>

          <div style={{
            textAlign: 'center',
            padding: '2rem',
            background: 'rgba(92, 112, 128, 0.4)',
            borderRadius: '1rem',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: 'rgba(0, 191, 255, 0.2)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <Award size={32} color="#00BFFF" />
            </div>
            <h3 style={{ fontFamily: "'Open Sans', sans-serif", marginBottom: '0.75rem', color: '#FFFFFF', fontWeight: 600, fontSize: '1.25rem' }}>Our Approach</h3>
            <p style={{ fontFamily: "'Open Sans', sans-serif", lineHeight: '1.7', color: '#E0E0E0', fontSize: '0.95rem' }}>We combine education with practical application, providing both knowledge and tools. Through our high-powered antioxidant hydration systems and non-toxic cleaning solutions, we help neutralize oxidative stress at its source. Our ultimate vision is simple yet profound: to demonstrate to the world that Electrolysed Reduced Water has the power to enhance every life it touches, one person and one program at a time.</p>
          </div>
        </div>
      </div>

      <style>{`
        @media (min-width: 768px) {
          .about-grid {
            grid-template-columns: 1fr 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};
